'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useOrderStore } from '@/store/useOrderStore';
import { calculateMultiItemDiscounts } from '@/lib/price-calculator';
import { Loader2 } from 'lucide-react';

interface Props {
  onComplete: () => void;
}

export default function Step3PriceEstimate({ onComplete }: Props) {
  const { itemInfo, items, origin, destination, priceBreakdown, setPriceBreakdown, distanceKm, setDistanceKm } = useOrderStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 가격 계산에 영향을 주는 원시값만 추출
  const originLat = origin?.lat;
  const originLng = origin?.lng;
  const originHasElevator = origin?.hasElevator;
  const originFloor = origin?.floor;
  const destLat = destination?.lat;
  const destLng = destination?.lng;
  const destHasElevator = destination?.hasElevator;
  const destFloor = destination?.floor;
  // 다물품 할인 적용
  const discountMap = useMemo(() => calculateMultiItemDiscounts(items), [items]);

  // items 배열의 할인 적용 합산 가격 사용 (items가 비어있으면 현재 itemInfo 사용 — 하위 호환)
  const itemBasePrice = items.length > 0
    ? items.reduce((sum, i) => {
        const d = discountMap.get(i.uid);
        return sum + (d ? d.discountedPrice : i.basePrice);
      }, 0)
    : itemInfo.basePrice;
  const itemId = items.length > 0 ? items[0].itemId : itemInfo.itemId;

  const calcKey = `${itemId}-${itemBasePrice}-${originLat}-${originLng}-${originHasElevator}-${originFloor}-${destLat}-${destLng}-${destHasElevator}-${destFloor}`;

  // store에 가격이 이미 있으면 현재 calcKey로 초기화 (remount 시 불필요한 재계산 방지)
  const fetchedKey = useRef(priceBreakdown ? calcKey : '');

  useEffect(() => {
    if (!itemId || !originLat || !originLng || !destLat || !destLng) return;
    if (fetchedKey.current === calcKey) return; // 이미 같은 조건으로 계산 완료

    fetchedKey.current = calcKey;

    const calculatePrice = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch('/api/calculate-price', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            itemId,
            selectedOptions: itemInfo.options,
            itemBasePrice,
            origin: {
              lat: originLat,
              lng: originLng,
              hasElevator: originHasElevator,
              floor: originFloor,
            },
            destination: {
              lat: destLat,
              lng: destLng,
              hasElevator: destHasElevator,
              floor: destFloor,
            },
          }),
        });

        if (!res.ok) throw new Error('계산 실패');

        const data = await res.json();
        setDistanceKm(data.distanceKm);
        setPriceBreakdown(data.breakdown);
      } catch (err) {
        setError('운송비 계산 중 오류가 발생했습니다.');
        fetchedKey.current = ''; // 실패 시 재시도 가능하도록
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    calculatePrice();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcKey]);

  const formatPrice = (price: number) => price.toLocaleString() + '원';
  const formatRate = (rate: number) => '+' + Math.round(rate * 100) + '%';

  // 커스텀 물품인 경우
  if (itemInfo.isCustom) {
    return (
      <div className="space-y-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-semibold text-yellow-800 mb-2">커스텀 물품 견적 안내</h4>
          <p className="text-sm text-yellow-700">
            직접 등록하신 물품은 매니저가 확인 후 운송비를 별도 안내드립니다.
            연락처를 입력해주시면 문자로 안내됩니다.
          </p>
        </div>
        <Button onClick={onComplete} className="w-full" size="lg">
          견적 요청하기
        </Button>
      </div>
    );
  }

  if (error && !priceBreakdown) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500 mb-4">{error}</p>
        <Button variant="outline" onClick={() => { fetchedKey.current = ''; setError(null); }}>
          다시 시도
        </Button>
      </div>
    );
  }

  if (!priceBreakdown) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A3A5C]" />
        <p className="mt-4 text-gray-500">운송비를 계산하고 있습니다...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 안내문 */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
        <p className="text-sm text-yellow-800">
          ℹ️ 최종 운송비는 일정 등 세부정보 입력 후 확정됩니다.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        {/* 물품 운송비 */}
        {items.length >= 2 ? (
          <>
            {items.map((item) => {
              const disc = discountMap.get(item.uid);
              const hasDiscount = disc && disc.discountRate > 0;
              return (
                <div key={item.uid} className="flex justify-between text-sm">
                  <span className="text-gray-600">{item.displayName}</span>
                  {hasDiscount ? (
                    <span className="font-medium flex items-center gap-2">
                      <span className="text-gray-400 line-through text-xs">{formatPrice(item.basePrice)}</span>
                      <span className="text-red-500 text-xs">-{Math.round(disc.discountRate * 100)}%</span>
                      <span>{formatPrice(disc.discountedPrice)}</span>
                    </span>
                  ) : (
                    <span className="font-medium">{formatPrice(item.basePrice)}</span>
                  )}
                </div>
              );
            })}
            <div className="flex justify-between border-t border-gray-100 pt-2">
              <span className="text-gray-600">물품 운송비 소계</span>
              <span className="font-medium">{formatPrice(priceBreakdown.itemBasePrice)}</span>
            </div>
          </>
        ) : (
          <div className="flex justify-between">
            <span className="text-gray-600">물품 운송비 ({items.length > 0 ? items[0].displayName : itemInfo.category})</span>
            <span className="font-medium">{formatPrice(priceBreakdown.itemBasePrice)}</span>
          </div>
        )}

        {/* 거리할증 */}
        {priceBreakdown.distanceSurcharge > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-600">
              거리할증 ({distanceKm}km, {formatRate(priceBreakdown.distanceSurchargeRate)})
            </span>
            <span className="font-medium">+{formatPrice(priceBreakdown.distanceSurcharge)}</span>
          </div>
        )}

        {/* 계단할증 - 출발지 */}
        {priceBreakdown.stairSurchargeOrigin > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-600">
              계단할증 (출발지 {origin?.floor}층, {formatRate(priceBreakdown.stairSurchargeOriginRate)})
            </span>
            <span className="font-medium">+{formatPrice(priceBreakdown.stairSurchargeOrigin)}</span>
          </div>
        )}

        {/* 계단할증 - 도착지 */}
        {priceBreakdown.stairSurchargeDest > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-600">
              계단할증 (도착지 {destination?.floor}층, {formatRate(priceBreakdown.stairSurchargeDestRate)})
            </span>
            <span className="font-medium">+{formatPrice(priceBreakdown.stairSurchargeDest)}</span>
          </div>
        )}

        {/* 구분선 */}
        <div className="border-t border-gray-200 my-2" />

        {/* 합계 */}
        <div className="flex justify-between">
          <span className="font-bold text-[#1A3A5C]">기본 운송비</span>
          <span className="font-bold text-xl text-[#1A3A5C]">
            {formatPrice(priceBreakdown.totalPrice)}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-500 px-1">
        <span>* 위 서비스의 최대 이용 기간은 1주일 입니다.</span>
        <a
          href="/terms#제5조"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#1A3A5C] underline underline-offset-2 whitespace-nowrap ml-2"
        >
          환불 규정 안내
        </a>
      </div>

      <Button onClick={onComplete} className="w-full" size="lg">
        다음 단계로
      </Button>
    </div>
  );
}
